/**
 * Native image viewer — for formats the platform decodes and the app does not
 * (HEIC/HEIF today) (§20, §32).
 *
 * The picture is an `<img>` pointing at the file's own bytes; whether it
 * appears is the platform's answer, not ours. When the platform cannot decode
 * it — Chromium as shipped cannot decode HEIC — the `<img>` fails and this
 * shows the real dimensions read from the container plus an honest message,
 * rather than a blank frame pretending to be a photo (§19). Nothing is decoded
 * in-app, so there is no bundled HEVC decoder to license (§32).
 */
import { useEffect, useMemo, useState } from 'react';
import type { NativeImageModel } from '@moduviewer/handlers';
import { Icon } from '@moduviewer/design/icon';

export interface NativeImageViewerProps {
  readonly model: NativeImageModel;
  readonly bytes: Uint8Array;
}

export function NativeImageViewer({ model, bytes }: NativeImageViewerProps): React.JSX.Element {
  const [failed, setFailed] = useState(false);

  // Rendered from memory, never the network (§29); revoked when the view changes.
  const url = useMemo(
    () => URL.createObjectURL(new Blob([bytes as BlobPart], { type: model.mimeType })),
    [bytes, model.mimeType],
  );
  useEffect(() => () => URL.revokeObjectURL(url), [url]);
  useEffect(() => { setFailed(false); }, [url]);

  const dimensions = model.width && model.height ? `${model.width}×${model.height}` : null;

  return (
    <div className="native-image">
      {failed ? (
        <div className="native-image__fallback" role="status">
          <Icon name="file-image" size={44} className="native-image__icon" />
          <p className="native-image__msg">
            이 기기에서는 HEIC 이미지를 표시할 수 없습니다. 코덱이 없어 사진을 디코드하지 못합니다 (§20).
          </p>
          <dl className="native-image__specs tnum">
            {dimensions ? <div><dt>해상도</dt><dd>{dimensions}</dd></div> : null}
            {model.brand ? <div><dt>형식</dt><dd>HEIF ({model.brand.trim()})</dd></div> : null}
            {model.rotationDeg ? <div><dt>회전</dt><dd>{model.rotationDeg}°</dd></div> : null}
          </dl>
        </div>
      ) : (
        <img
          className="native-image__img"
          src={url}
          alt={dimensions ? `HEIC 이미지 ${dimensions}` : 'HEIC 이미지'}
          style={{ transform: model.rotationDeg ? `rotate(${model.rotationDeg}deg)` : undefined }}
          onError={() => setFailed(true)}
        />
      )}
    </div>
  );
}
